/*
 * WebMCPAdapter — the ONLY place in this application that touches the browser's
 * WebMCP API.
 *
 * Everything else talks to the ToolRegistry. That separation is what lets the
 * debug panel and Playwright exercise exactly the same tool code an agent runs.
 *
 * API shape verified 2026-09-01 against the published type definitions of
 * @mcp-b/webmcp-types 5.1.0 (`src/model-context.ts`, `src/tool.ts`) and the
 * @mcp-b/webmcp-polyfill 5.1.0 README — not from memory:
 *
 *   document.modelContext.registerTool(tool, { signal, exposedTo }) -> Promise<void>
 *   document.modelContext.getTools({ fromOrigins }) -> Promise<RegisteredTool[]>
 *   document.modelContext.executeTool(tool, inputJsonString, { signal })  // Chromium extension, feature-detect
 *   document.modelContext + 'toolchange' event
 *
 *   tool = { name, title?, description, inputSchema?, annotations?, execute(input) }
 *
 * Notes that cost real debugging time if forgotten:
 *   - `document.modelContext` is canonical in the spec and Chromium main, but Chrome 149/150
 *     shipped `navigator.modelContext` ONLY — see resolveModelContext() below.
 *   - registerTool rejects on duplicate names / invalid descriptors from Chrome 151 on, and
 *     throws synchronously on 149/150.
 *   - a tool is unregistered by aborting the AbortSignal given at registration —
 *     there is no unregisterTool().
 *   - RegisteredTool.inputSchema is a JSON string on Chrome 149-153 and an object
 *     from Chrome 154 on, so any reader must branch on typeof.
 */

import { describeEnvironment } from './environment.js'

export const WebMcpTransport = {
  NATIVE: 'native',
  POLYFILL: 'polyfill',
  UNAVAILABLE: 'unavailable'
}

const LOG_PREFIX = '[webmcp]'

/**
 * Resolve the browser's ModelContext, whichever accessor this build exposes.
 *
 * The accessor moved during the Chrome origin trial and BOTH are in the wild:
 *   Chrome 149/150 -> navigator.modelContext only
 *   Chrome 151+    -> document.modelContext (navigator kept as a deprecated alias in 150-152)
 *   spec + Chromium main -> document.modelContext only
 * ChatGPT Desktop embeds its own Chromium, so we cannot assume a version.
 *
 * @returns {{context: object, accessor: 'document'|'navigator'}|null}
 */
export function resolveModelContext () {
  if (typeof document !== 'undefined' && document.modelContext) {
    return { context: document.modelContext, accessor: 'document' }
  }
  if (typeof navigator !== 'undefined' && navigator.modelContext) {
    return { context: navigator.modelContext, accessor: 'navigator' }
  }
  return null
}

/** True when the browser itself implements WebMCP (i.e. before we polyfill). */
export function detectNativeWebMcp () {
  return resolveModelContext() !== null
}

export class WebMcpAdapter {
  /**
   * @param {import('./toolRegistry.js').ToolRegistry} registry
   */
  constructor (registry, { logger = console } = {}) {
    this.registry = registry
    this.logger = logger
    this.transport = WebMcpTransport.UNAVAILABLE
    this.nativeAvailable = false
    this.accessor = null
    // True while connect() is still running. The polyfill is a dynamic import, so
    // there is a real window in which nothing is registered yet; without this the
    // diagnostics panel shows a red "not supported" for a second on every load.
    this.connecting = false
    this.error = null
    this.registeredNames = []
    this._controller = null
    this._listeners = new Set()
  }

  get available () {
    return this.transport !== WebMcpTransport.UNAVAILABLE
  }

  /** Snapshot for the diagnostics panel. Contains nothing secret. */
  status () {
    return {
      transport: this.transport,
      available: this.available,
      connecting: this.connecting,
      nativeAvailable: this.nativeAvailable,
      accessor: this.accessor,
      registeredToolNames: [...this.registeredNames],
      error: this.error ? { message: this.error.message, name: this.error.name } : null,
      environment: describeEnvironment(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
    }
  }

  subscribe (listener) {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  _emit () {
    for (const l of this._listeners) {
      try { l(this.status()) } catch (e) { this.logger.error(LOG_PREFIX, 'status listener failed', e) }
    }
  }

  /**
   * Detect WebMCP (optionally installing the polyfill) and publish every tool
   * currently in the registry. Never throws: an application must keep working
   * in a browser that has no WebMCP at all.
   *
   * @param {{installPolyfill?: boolean}} [options]
   * @returns {Promise<ReturnType<WebMcpAdapter['status']>>}
   */
  async connect ({ installPolyfill = true } = {}) {
    this.connecting = true
    this._emit()
    try {
      this.nativeAvailable = detectNativeWebMcp()

      if (this.nativeAvailable) {
        this.transport = WebMcpTransport.NATIVE
        this.accessor = resolveModelContext().accessor
        this.logger.info(`${LOG_PREFIX} native WebMCP detected via ${this.accessor}.modelContext`)
      } else if (installPolyfill) {
        // Dynamic import so a browser that already has WebMCP never pays for it,
        // and so a polyfill failure cannot break the application's first paint.
        const { initializeWebMCPPolyfill } = await import('@mcp-b/webmcp-polyfill')
        initializeWebMCPPolyfill()
        if (detectNativeWebMcp()) {
          this.transport = WebMcpTransport.POLYFILL
          this.logger.info(
            `${LOG_PREFIX} this browser has no native WebMCP — installed @mcp-b/webmcp-polyfill.\n` +
            `${LOG_PREFIX} Tools are registered and callable from this page (debug panel, tests),\n` +
            `${LOG_PREFIX} but no AI client is attached. For a real agent connection use ChatGPT\n` +
            `${LOG_PREFIX} Desktop's built-in browser, or enable chrome://flags/#enable-webmcp-testing.`
          )
        } else {
          this.transport = WebMcpTransport.UNAVAILABLE
          this.logger.warn(`${LOG_PREFIX} polyfill loaded but document.modelContext is still absent`)
        }
      } else {
        this.logger.warn(`${LOG_PREFIX} no native WebMCP and polyfill disabled — agent tools are offline`)
      }

      if (this.available) await this._publishTools()
    } catch (err) {
      this.error = err
      this.transport = WebMcpTransport.UNAVAILABLE
      this.logger.warn(`${LOG_PREFIX} WebMCP unavailable — the application continues without agent tools.`, err)
    } finally {
      this.connecting = false
    }

    this._emit()
    return this.status()
  }

  /** Abort every registration this adapter made. */
  disconnect () {
    if (this._controller) this._controller.abort()
    this._controller = null
    this.registeredNames = []
    this.transport = detectNativeWebMcp() ? this.transport : WebMcpTransport.UNAVAILABLE
    this._emit()
  }

  /** Re-publish after the registry's tool set changed. */
  async refresh () {
    if (!this.available) return this.status()
    if (this._controller) this._controller.abort()
    await this._publishTools()
    this._emit()
    return this.status()
  }

  _modelContext () {
    const resolved = resolveModelContext()
    if (!resolved) throw new Error('WebMCP is not available in this browser')
    this.accessor = resolved.accessor
    return resolved.context
  }

  async _publishTools () {
    this._controller = new AbortController()
    const signal = this._controller.signal
    const names = []
    const ctx = this._modelContext()

    for (const tool of this.registry.list()) {
      try {
        // registerTool returns a promise from Chrome 151 on, and throws
        // synchronously on 149/150 — awaiting inside try/catch covers both.
        await ctx.registerTool({
          name: tool.name,
          title: tool.title,
          description: tool.description,
          inputSchema: tool.inputSchema,
          annotations: { readOnlyHint: !!tool.readOnly },
          execute: (input) => this.registry.invoke(tool.name, input || {}, { source: 'webmcp' })
        }, { signal })
        names.push(tool.name)
      } catch (err) {
        this.logger.error(`${LOG_PREFIX} failed to register tool "${tool.name}"`, err)
      }
    }

    this.registeredNames = names
    this.logger.info(`${LOG_PREFIX} registered ${names.length} tool(s): ${names.join(', ') || '(none)'}`)
  }

  /**
   * What the browser itself reports — the agent's view, not ours. Used by the
   * diagnostics panel to prove registration actually reached the browser.
   */
  async browserTools () {
    if (!this.available) return []
    const tools = await this._modelContext().getTools()
    return tools.map((t) => ({
      name: t.name,
      title: t.title || t.name,
      description: t.description,
      origin: t.origin,
      annotations: t.annotations,
      // Chrome 149-153 hand back a serialized schema; 154+ hand back an object.
      inputSchema: typeof t.inputSchema === 'string' ? safeParse(t.inputSchema) : t.inputSchema
    }))
  }

  /**
   * Round-trip a call through the browser's own WebMCP surface rather than
   * straight into the registry. This is how we prove the WebMCP path works
   * without an AI client.
   *
   * `executeTool` must be feature-detected, and its second argument is NOT
   * stable across implementations: Chromium and @mcp-b/webmcp-polyfill take a
   * JSON **string**, while the Community Group draft IDL takes an **object**.
   * Passing the wrong one is a TypeError, so try the string form first (what
   * ships today) and fall back to the object form for a spec-conformant build.
   */
  async executeViaBrowser (name, args = {}) {
    if (!this.available) throw new Error('WebMCP is not available in this browser')
    const ctx = this._modelContext()
    if (typeof ctx.executeTool !== 'function') {
      throw new Error('document.modelContext.executeTool() is not implemented here (Chromium extension)')
    }
    const tools = await ctx.getTools()
    const tool = tools.find((t) => t.name === name)
    if (!tool) throw new Error(`Tool "${name}" is not registered with the browser`)

    let raw
    try {
      raw = await ctx.executeTool(tool, JSON.stringify(args))
    } catch (err) {
      if (!(err instanceof TypeError)) throw err
      this.logger.info(`${LOG_PREFIX} executeTool rejected the JSON-string form, retrying with the object form`)
      raw = await ctx.executeTool(tool, args)
    }
    return typeof raw === 'string' ? safeParse(raw) : raw
  }
}

function safeParse (text) {
  try { return JSON.parse(text) } catch { return text }
}
